import { License } from '@/types/consultant-registration.types';
import { FormField } from '@/components/broker-registration/FormField';
import { TextInput } from '@/components/broker-registration/TextInput';
import {
  CANADIAN_PROVINCES,
  USA_STATES,
  EUROPE_COUNTRIES,
} from '@/constants/consultant-registration.constants';
import { useLanguage } from '@/contexts/LanguageContext';

interface LicenseBlockProps {
  license: License;
  index: number;
  onUpdate: (index: number, updates: Partial<License>) => void;
  onRemove: (index: number) => void;
  errors: Record<string, string>;
  clearError: (field: string) => void;
  canRemove: boolean;
}

export function LicenseBlock({
  license,
  index,
  onUpdate,
  onRemove,
  errors,
  clearError,
  canRemove,
}: LicenseBlockProps) {
  const { t } = useLanguage();

  const toggleJurisdictionSection = (section: 'canada' | 'usa' | 'europe') => {
    const showKey = `show${section.charAt(0).toUpperCase()}${section.slice(1)}` as keyof License;
    const newValue = !license[showKey];

    onUpdate(index, {
      [showKey]: newValue,
      jurisdictions: {
        ...license.jurisdictions,
        [section]: newValue ? license.jurisdictions[section] : [],
      },
    });
  };

  const toggleJurisdiction = (region: 'canada' | 'usa' | 'europe', value: string) => {
    const currentJurisdictions = license.jurisdictions[region] || [];
    const newJurisdictions = currentJurisdictions.includes(value)
      ? currentJurisdictions.filter((j) => j !== value)
      : [...currentJurisdictions, value];

    onUpdate(index, {
      jurisdictions: {
        ...license.jurisdictions,
        [region]: newJurisdictions,
      },
    });
  };

  const selectAllJurisdictions = (region: 'canada' | 'usa' | 'europe', values: readonly string[], checked: boolean) => {
    onUpdate(index, {
      jurisdictions: {
        ...license.jurisdictions,
        [region]: checked ? [...values] : [],
      },
    });
  };

  const allCanadaChecked = license.jurisdictions.canada.length === CANADIAN_PROVINCES.length;
  const allUsaChecked = license.jurisdictions.usa.length === USA_STATES.length;

  return (
    <div className="border-2 border-blue-400/30 rounded-lg p-4 mb-4 bg-gray-700/50">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-lg font-semibold text-white">
          {t('consultant_registration.step_two.license')} {index + 1}
        </h4>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-sm bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors"
          >
            {t('consultant_registration.step_two.remove')}
          </button>
        )}
      </div>

      <div className="space-y-4">
        <FormField
          label={t('consultant_registration.step_two.license_details')}
          error={errors[`license${index}_details`]}
          required
        >
          <textarea
            value={license.details}
            onChange={(e) => {
              onUpdate(index, { details: e.target.value });
              clearError(`license${index}_details`);
            }}
            rows={2}
            placeholder={t('consultant_registration.step_two.license_details_placeholder')}
            className={`w-full rounded-md border ${
              errors[`license${index}_details`] ? 'border-red-500' : 'border-gray-600'
            } bg-gray-700 px-4 py-2 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
          />
        </FormField>

        <FormField
          label={t('consultant_registration.step_two.license_number')}
          error={errors[`license${index}_number`]}
          required
        >
          <TextInput
            value={license.number}
            onChange={(value) => {
              onUpdate(index, { number: value });
              clearError(`license${index}_number`);
            }}
            placeholder={t('consultant_registration.step_two.license_number_placeholder')}
            error={!!errors[`license${index}_number`]}
          />
        </FormField>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-white/95">
            {t('consultant_registration.step_two.jurisdictions_covered')}
          </label>
          <p className="text-xs text-gray-400 italic">
            {t('consultant_registration.step_two.jurisdictions_hint')}
          </p>

          {/* Canada */}
          <div className="border rounded-md p-3 bg-gray-700/50">
            <label className="flex items-center gap-2 text-sm font-medium text-white/95 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={license.showCanada || false}
                onChange={() => toggleJurisdictionSection('canada')}
                className="rounded text-[#CEAB65]"
              />
              {t('consultant_registration.options.jurisdictions.canada')}
            </label>
            {license.showCanada && (
              <div className="pl-6 space-y-2">
                <label className="flex items-center gap-2 text-sm text-white/90">
                  <input
                    type="checkbox"
                    checked={allCanadaChecked}
                    onChange={(e) => selectAllJurisdictions('canada', CANADIAN_PROVINCES, e.target.checked)}
                    className="rounded text-[#CEAB65]"
                  />
                  {t('consultant_registration.step_two.all_canada')}
                </label>
                <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto border rounded p-2 bg-black/10">
                  {CANADIAN_PROVINCES.map((prov) => (
                    <label key={prov} className="flex items-center gap-1 text-xs text-white/90">
                      <input
                        type="checkbox"
                        checked={license.jurisdictions.canada.includes(prov)}
                        onChange={() => toggleJurisdiction('canada', prov)}
                        className="rounded text-[#CEAB65]"
                      />
                      {prov}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* USA */}
          <div className="border rounded-md p-3 bg-gray-700/50">
            <label className="flex items-center gap-2 text-sm font-medium text-white/95 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={license.showUsa || false}
                onChange={() => toggleJurisdictionSection('usa')}
                className="rounded text-[#CEAB65]"
              />
              {t('consultant_registration.options.jurisdictions.usa')}
            </label>
            {license.showUsa && (
              <div className="pl-6 space-y-2">
                <label className="flex items-center gap-2 text-sm text-white/90">
                  <input
                    type="checkbox"
                    checked={allUsaChecked}
                    onChange={(e) => selectAllJurisdictions('usa', USA_STATES, e.target.checked)}
                    className="rounded text-[#CEAB65]"
                  />
                  {t('consultant_registration.step_two.all_usa')}
                </label>
                <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto border rounded p-2 bg-black/10">
                  {USA_STATES.map((state) => (
                    <label key={state} className="flex items-center gap-1 text-xs text-white/90">
                      <input
                        type="checkbox"
                        checked={license.jurisdictions.usa.includes(state)}
                        onChange={() => toggleJurisdiction('usa', state)}
                        className="rounded text-[#CEAB65]"
                      />
                      {state}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Europe */}
          <div className="border rounded-md p-3 bg-gray-700/50">
            <label className="flex items-center gap-2 text-sm font-medium text-white/95 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={license.showEurope || false}
                onChange={() => toggleJurisdictionSection('europe')}
                className="rounded text-[#CEAB65]"
              />
              {t('consultant_registration.options.jurisdictions.europe')}
            </label>
            {license.showEurope && (
              <div className="pl-6">
                <div className="grid grid-cols-2 gap-2">
                  {EUROPE_COUNTRIES.map((country) => (
                    <label key={country} className="flex items-center gap-1 text-xs text-white/90">
                      <input
                        type="checkbox"
                        checked={license.jurisdictions.europe.includes(country)}
                        onChange={() => toggleJurisdiction('europe', country)}
                        className="rounded text-[#CEAB65]"
                      />
                      {country}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Other Jurisdictions */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-white/95">
              {t('consultant_registration.step_two.other_jurisdictions')}
            </label>
            <input
              type="text"
              value={license.jurisdictions.other}
              onChange={(e) =>
                onUpdate(index, {
                  jurisdictions: {
                    ...license.jurisdictions,
                    other: e.target.value,
                  },
                })
              }
              placeholder={t('consultant_registration.step_two.other_jurisdictions_placeholder')}
              className="w-full rounded-md border border-gray-600 bg-gray-700 px-4 py-2 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
